/**
 * ClimateSafe Authentication Context (Tim-Combo StatusProvider Pattern)
 *
 * Following Equilateral-Standards: tim_combo_frontend_auth_pattern.md
 *
 * Key principles:
 * - Single source of truth for authentication state
 * - authenticate() handles Cognito AND user data
 * - State updates trigger re-renders automatically
 * - No callback patterns for state updates
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { CognitoUser, AuthenticationDetails, CognitoUserSession } from 'amazon-cognito-identity-js';
import { seawaterUserPool } from '../auth/userPool';

interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  errorMessage: string | null;
  user: {
    email: string | null;
    username: string | null;
    subscriptionTier: 'free' | 'premium' | 'professional';
  } | null;
  usageInfo: {
    canMake: boolean;
    remaining: number;
    limit: number;
    resetDate?: Date;
    assessmentsUsed: number;
    assessmentsLimit: number;
    daysUntilReset: number;
  };
}

interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  upgradeSubscription: (tier: string) => Promise<{ success: boolean; error?: string }>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthContextProvider');
  }
  return context;
};

export const AuthContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    errorMessage: null,
    user: null,
    usageInfo: {
      canMake: true,
      remaining: 3,
      limit: 3,
      assessmentsUsed: 0,
      assessmentsLimit: 3,
      daysUntilReset: 30
    }
  });

  // Check for existing session on mount (Tim-Combo pattern)
  useEffect(() => {
    const checkSession = async () => {
      try {
        const cognitoUser = seawaterUserPool.getCurrentUser();
        if (cognitoUser) {
          cognitoUser.getSession((err: any, session: CognitoUserSession | null) => {
            if (err || !session || !session.isValid()) {
              // Clear invalid tokens
              localStorage.removeItem('idToken');
              localStorage.removeItem('accessToken');
              setState(prev => ({
                ...prev,
                isAuthenticated: false,
                isLoading: false,
                user: null
              }));
            } else {
              // Valid session - restore state
              const idToken = session.getIdToken();
              const jwt = idToken.getJwtToken();
              const email = idToken.payload.email;

              // Store tokens (Tim-Combo pattern)
              localStorage.setItem('idToken', jwt);
              localStorage.setItem('accessToken', session.getAccessToken().getJwtToken());

              setState(prev => ({
                ...prev,
                isAuthenticated: true,
                isLoading: false,
                user: {
                  email: email,
                  username: cognitoUser.getUsername(),
                  subscriptionTier: (localStorage.getItem('subscriptionTier') as any) || 'free'
                }
              }));
            }
          });
        } else {
          setState(prev => ({ ...prev, isLoading: false }));
        }
      } catch (error) {
        console.error('[AuthContext] Session check error:', error);
        setState(prev => ({ ...prev, isLoading: false }));
      }
    };

    checkSession();
  }, []);

  // CRITICAL: authenticate() handles Cognito AND updates state (Tim-Combo pattern)
  const login = useCallback(async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    setState(prev => ({ ...prev, isLoading: true, errorMessage: null }));

    try {
      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: seawaterUserPool
      });

      const authDetails = new AuthenticationDetails({
        Username: email,
        Password: password
      });

      // Step 1: Authenticate with Cognito
      const session = await new Promise<CognitoUserSession>((resolve, reject) => {
        cognitoUser.authenticateUser(authDetails, {
          onSuccess: (session) => resolve(session),
          onFailure: (err) => reject(err)
        });
      });

      // Step 2: Store tokens (Tim-Combo pattern - localStorage, not cookies)
      const idToken = session.getIdToken().getJwtToken();
      const accessToken = session.getAccessToken().getJwtToken();
      localStorage.setItem('idToken', idToken);
      localStorage.setItem('accessToken', accessToken);

      // Step 3: Update auth state (triggers routing via useEffect in Login)
      setState(prev => ({
        ...prev,
        isAuthenticated: true,
        isLoading: false,
        errorMessage: null,
        user: {
          email: email,
          username: cognitoUser.getUsername(),
          subscriptionTier: 'free'
        }
      }));

      return { success: true };

    } catch (error: any) {
      console.error('[AuthContext] Authentication failed:', error);

      let errorMessage = 'Login failed';
      switch (error?.code) {
        case 'NotAuthorizedException':
          errorMessage = 'Incorrect email or password';
          break;
        case 'UserNotFoundException':
          errorMessage = 'User does not exist';
          break;
        case 'UserNotConfirmedException':
          errorMessage = 'Please confirm your email address first';
          break;
        default:
          errorMessage = error.message || 'Login failed';
      }

      setState(prev => ({
        ...prev,
        isLoading: false,
        isAuthenticated: false,
        errorMessage: errorMessage
      }));

      return { success: false, error: errorMessage };
    }
  }, []);

  // Logout clears tokens and state (Tim-Combo pattern)
  const logout = useCallback(() => {
    const cognitoUser = seawaterUserPool.getCurrentUser();
    if (cognitoUser) {
      cognitoUser.signOut();
    }

    // Clear tokens (Tim-Combo pattern)
    localStorage.removeItem('idToken');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('subscriptionTier');

    setState({
      isAuthenticated: false,
      isLoading: false,
      errorMessage: null,
      user: null,
      usageInfo: {
        canMake: true,
        remaining: 3,
        limit: 3,
        assessmentsUsed: 0,
        assessmentsLimit: 3,
        daysUntilReset: 30
      }
    });
  }, []);

  const upgradeSubscription = async (tier: string) => {
    // TODO: Implement actual subscription upgrade
    console.log('Upgrading to:', tier);
    localStorage.setItem('subscriptionTier', tier);
    return { success: true };
  };

  const value: AuthContextValue = {
    ...state,
    login,
    logout,
    upgradeSubscription
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
